import {
  $,
  api,
  appState,
  escapeHtml,
  friendlyPlatform,
} from './app-state';
import {
  readSession,
  setAccountTab,
  renderLoggedIn,
  renderLoggedOut,
  accountLogout,
  saveSession,
} from './account';
import { startSession } from './session';
import { newController } from './app';
import { getCurrentServerUrl } from '../shared/constants';
import { readSessionMode } from '../shared/ui/session-mode';
import {
  isDeviceFavorite,
  markDeviceConnected,
  sortDevices,
  toggleDeviceFavorite,
} from '../shared/device-preferences';

function setDeviceRefreshBusy(busy: boolean): void {
  for (const id of ['account-refresh-btn', 'devices-refresh-btn']) {
    const button = $(id) as HTMLButtonElement;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
    button.title = busy ? '正在更新设备' : '刷新设备';
  }
}

function scheduleDevicesRefresh(): void {
  if (appState.devicesRefreshTimer !== null) window.clearTimeout(appState.devicesRefreshTimer);
  if (!$('devices-pane').classList.contains('hidden') && api.token) {
    appState.devicesRefreshTimer = window.setTimeout(() => {
      appState.devicesRefreshTimer = null;
      void loadDevices(true);
    }, 5_000);
  } else {
    appState.devicesRefreshTimer = null;
  }
}

async function loadDevices(silent = false): Promise<void> {
  const pane = $('devices-pane');
  const status = $('devices-status');
  const list = $('devices-list');
  const empty = $('devices-empty');
  const count = $('devices-count');
  if (!api.token || !api.serverUrl) {
    renderLoggedOut();
    return;
  }
  if (appState.devicesLoading) return;
  appState.devicesLoading = true;
  pane.classList.remove('hidden');
  const hasVisibleDevices = list.children.length > 0;
  if (!silent) {
    status.textContent = hasVisibleDevices ? '正在更新设备…' : '加载设备…';
    if (!hasVisibleDevices) {
      list.innerHTML = '';
      empty.classList.add('hidden');
      count.textContent = '';
    }
  }
  setDeviceRefreshBusy(true);
  const abort = new AbortController();
  const timeout = window.setTimeout(() => abort.abort(), 8_000);
  try {
    const r = await api.listDevices(abort.signal);
    if (r.unauthorized) {
      accountLogout('登录已过期，请重新登录', 'error');
      return;
    }
    if (!r.ok) {
      status.textContent = r.error || '获取设备失败';
      status.className = 'status err';
      return;
    }
    saveSession();
    renderLoggedIn();
    const localDeviceId = await api.getLocalDeviceId();
    const devices = sortDevices(r.devices);
    list.innerHTML = '';
    empty.classList.toggle('hidden', devices.length > 0);
    count.textContent = `${devices.length} 台`;
    if (devices.length === 0) {
      status.textContent = '';
      empty.classList.remove('hidden');
      return;
    }
    status.textContent = '';
    status.className = 'status';
    for (const d of devices) {
      const isBusy = Boolean(d.busy);
      const item = document.createElement('button');
      const isLocal = Boolean(localDeviceId && d.deviceId === localDeviceId);
      item.className = 'dev-item' + (d.online ? '' : ' off') + (isLocal ? ' local' : '') + (isBusy ? ' busy' : '');
      item.disabled = isLocal || !d.online || isBusy || !appState.connectionPreflightCanConnect;
      const platform = friendlyPlatform(d.platform || undefined);
      item.title = d.deviceName || '未命名设备';
      item.innerHTML =
        `<span class="dev-platform-icon" aria-hidden="true"><svg class="ic"><use href="#${platform.icon}"></use></svg></span>` +
        `<div class="name">${escapeHtml(d.deviceName || '未命名设备')}</div>` +
        `<div class="meta">${platform.label}${isLocal ? ' · 当前设备' : isBusy ? ' · 正在使用' : d.online ? ' · 点击连接' : ' · 打开 LinkDesk 后可连接'}</div>` +
        `<span class="tag ${isLocal ? 'local' : isBusy ? 'busy' : d.online ? 'on' : 'off'}">${isLocal ? '本机' : isBusy ? '使用中' : d.online ? '在线' : '离线'}</span>` +
        `<span class="dev-chevron" aria-hidden="true"><svg class="ic"><use href="#icon-chevron-right"></use></svg></span>`;
      if (d.online && !isLocal && !isBusy && appState.connectionPreflightCanConnect) {
        item.addEventListener('click', () => {
          markDeviceConnected(d.deviceId);
          const accessMode = readSessionMode('devices-mode-picker');
          const connect = async (): Promise<void> => {
            let sessionStarted = false;
            try {
              startSession(accessMode, connect);
              sessionStarted = true;
              const controller = newController();
              appState.controller = controller;
              await controller.connectCentral(api.token, d.deviceId, accessMode);
            } catch {
              if (sessionStarted) {
                const notice = document.getElementById('guest-error');
                appState.cancelSession?.();
                if (notice) notice.textContent = '暂时无法连接设备，请检查网络后重试。';
              }
            }
          };
          void connect();
        });
      }
      const row = document.createElement('div');
      row.className = 'device-row';
      row.appendChild(item);
      const favorite = document.createElement('button');
      favorite.type = 'button';
      favorite.className = 'dev-favorite';
      const updateFavorite = (value: boolean): void => {
        favorite.innerHTML = '<svg class="ic favorite-icon" aria-hidden="true"><use href="#icon-star"></use></svg>';
        favorite.setAttribute('aria-pressed', String(value));
        favorite.setAttribute('aria-label', value ? `取消收藏 ${d.deviceName || '这台设备'}` : `收藏 ${d.deviceName || '这台设备'}`);
        favorite.title = value ? '取消收藏' : '收藏设备';
      };
      updateFavorite(isDeviceFavorite(d.deviceId));
      favorite.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        updateFavorite(toggleDeviceFavorite(d.deviceId));
      });
      row.appendChild(favorite);
      if (isBusy && !isLocal) {
        const disconnect = document.createElement('button');
        disconnect.type = 'button';
        disconnect.className = 'dev-disconnect';
        disconnect.textContent = '结束';
        disconnect.title = `结束 ${d.deviceName || '这台设备'} 当前的连接`;
        disconnect.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          disconnect.disabled = true;
          disconnect.textContent = '结束中…';
          const result = await api.disconnectDevice(d.deviceId);
          if (!result.ok) {
            status.textContent = result.error || '结束连接失败';
            status.className = 'status err';
            disconnect.disabled = false;
            disconnect.textContent = '结束';
            return;
          }
          status.textContent = '连接已结束';
          status.className = 'status';
          await loadDevices(true);
        });
        row.appendChild(disconnect);
      }
      if (!isLocal) {
        const unbind = document.createElement('button');
        unbind.type = 'button';
        unbind.className = 'dev-unbind';
        unbind.textContent = '移除';
        unbind.title = `取消关联 ${d.deviceName || '这台设备'}`;
        unbind.setAttribute('aria-label', `取消关联 ${d.deviceName || '这台设备'}`);
        unbind.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          const warning = isBusy
            ? `“${d.deviceName || '这台设备'}”正在使用中。取消关联会结束连接，并让对方退出账号，确定继续吗？`
            : `取消关联“${d.deviceName || '这台设备'}”？这台设备将从你的账号中移除。`;
          if (!window.confirm(warning)) return;
          unbind.disabled = true;
          unbind.textContent = '移除中…';
          const result = await api.unbindDevice(d.deviceId);
          if (!result.ok) {
            status.textContent = result.error || '取消关联失败';
            status.className = 'status err';
            unbind.disabled = false;
            unbind.textContent = '移除';
            return;
          }
          status.textContent = '设备已取消关联';
          status.className = 'status';
          await loadDevices(true);
        });
        row.appendChild(unbind);
      }
      list.appendChild(row);
    }
    // 自动轮询保持安静，避免每 5 秒改写 aria-live 状态并反复打断读屏用户。
    if (!silent) status.textContent = '设备已更新';
  } catch (e) {
    status.textContent = hasVisibleDevices
      ? '更新失败，仍显示上次设备列表。点击“刷新设备”重试。'
      : e instanceof DOMException && e.name === 'AbortError'
        ? '请求超时，请点击“刷新设备”重试。'
        : e instanceof Error && e.message !== 'Failed to fetch'
        ? e.message
        : '暂时无法连接，请点击“刷新设备”重试。';
    status.className = 'status err';
  } finally {
    window.clearTimeout(timeout);
    appState.devicesLoading = false;
    setDeviceRefreshBusy(false);
    scheduleDevicesRefresh();
  }
}

function scheduleAccountValidation(): void {
  if (appState.accountValidationTimer !== null) window.clearTimeout(appState.accountValidationTimer);
  appState.accountValidationTimer = window.setTimeout(async () => {
    appState.accountValidationTimer = null;
    if (api.token && api.serverUrl && !appState.accountValidationBusy) {
      appState.accountValidationBusy = true;
      try {
        const me = await api.authMe();
        if (!me.ok && me.unauthorized) {
          accountLogout('登录已过期，请重新登录', 'error');
        } else if (me.ok) {
          api.username = me.username || api.username;
          saveSession();
          renderLoggedIn();
          if (!$('devices-pane').classList.contains('hidden')) await loadDevices(true);
        }
      } catch {
        // 短暂断网不清除本地令牌，下一轮继续校验。
      } finally {
        appState.accountValidationBusy = false;
      }
    }
    scheduleAccountValidation();
  }, 30_000);
}

async function restoreSession(): Promise<void> {
  const saved = readSession();
  if (!saved) {
    renderLoggedOut();
    return;
  }

  api.serverUrl = getCurrentServerUrl();
  api.token = saved.token;
  api.refreshToken = saved.refreshToken || '';
  api.username = saved.username;
  ($('acc-user') as HTMLInputElement).value = saved.username;
  setAccountTab();
  renderLoggedIn('正在恢复登录状态…', 'warn');

  const abort = new AbortController();
  const timeout = window.setTimeout(() => abort.abort(), 8000);
  try {
    const me = await api.authMe(abort.signal);
    if (!me.ok && me.unauthorized) {
      accountLogout('登录已过期，请重新登录', 'error');
      return;
    }
    if (!me.ok) throw new Error(me.error || '暂时无法连接');
    api.username = me.username || api.username;
    saveSession();
    renderLoggedIn();
    await loadDevices();
  } catch (e) {
    // 网络短暂不可用时保留令牌，避免刷新页面就被迫重新登录。
    renderLoggedIn('登录状态已保留，恢复连接后会自动更新设备。', 'warn');
    $('devices-pane').classList.remove('hidden');
    $('devices-status').textContent = e instanceof DOMException && e.name === 'AbortError'
      ? '连接超时，请点击“刷新设备”重试。'
      : '暂时无法连接，请点击“刷新设备”重试。';
    $('devices-status').className = 'status err';
  } finally {
    window.clearTimeout(timeout);
  }
}

export {
  scheduleDevicesRefresh,
  loadDevices,
  scheduleAccountValidation,
  restoreSession,
};
